import { useCallback, useState } from "react";

import type { RoomPriceRankDailyStatus_RoomPriceRankPageQuery } from "@/lib/gql/__generated__/graphql";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { excludeKeys, type ExcludeKeys, type ProductRoomPriceRankDailyStatusFormModel } from "../../type";
import { roomPriceRankDailyStatusZodSchema } from "../../validation-schema";
import { convertToInputModel } from "./composition/convert-to-input-model";

import type { RoomPriceRankDailyStatusInputZodSchemaType } from "../../validation-schema";
// eslint-disable-next-line import/no-restricted-paths
import type { ApolloQueryResult } from "@apollo/client";

type Props = {
	hotelID: string;
	roomPriceRankDailyStatus: RoomPriceRankDailyStatus_RoomPriceRankPageQuery;
	onRefetch: (props: {
		hotelID: string;
		startDt: string;
		endDt: string;
	}) => Promise<ApolloQueryResult<RoomPriceRankDailyStatus_RoomPriceRankPageQuery | undefined>>;
};

type ViewModel = ProductRoomPriceRankDailyStatusFormModel["ViewModel"];
type InputModel = ProductRoomPriceRankDailyStatusFormModel["InputModel"];
type QueryData = ProductRoomPriceRankDailyStatusFormModel["QueryData"];

export type ProductRoomPriceRankDailyStatusOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export const useRoomPriceRankDailyStatusModel = ({ hotelID, roomPriceRankDailyStatus, onRefetch }: Props) => {
	const inputModel = convertToInputModel({ serverModel: roomPriceRankDailyStatus });

	const initialViewModel = inputModelToViewModelConverter<
		InputModel,
		ExcludeKeys,
		QueryData,
		RoomPriceRankDailyStatusInputZodSchemaType
	>({
		defaultInput: inputModel,
		initInput: undefined,
		currentInput: inputModel,
		excludeKeys,
		zodSchema: roomPriceRankDailyStatusZodSchema,
	});

	const [viewModel, setViewModel] = useState(initialViewModel);

	const onSetViewModel: ProductRoomPriceRankDailyStatusOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			};

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				RoomPriceRankDailyStatusInputZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				zodSchema: roomPriceRankDailyStatusZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[inputModel, viewModel],
	);

	const refetchRoomPriceRankDailyStatus = useCallback(
		async ({ startDt, endDt }: { startDt: string; endDt: string }) => {
			const { data } = await onRefetch({ hotelID, startDt, endDt });

			if (data) {
				const inputModel = convertToInputModel({ serverModel: data });
				const initialViewModel = inputModelToViewModelConverter<
					InputModel,
					ExcludeKeys,
					QueryData,
					RoomPriceRankDailyStatusInputZodSchemaType
				>({
					defaultInput: inputModel,
					initInput: undefined,
					currentInput: inputModel,
					excludeKeys,
					zodSchema: roomPriceRankDailyStatusZodSchema,
				});
				setViewModel(initialViewModel);
			} else {
				notifyUserEventInfo({
					variant: "error",
					title: "データが取得できませんでした",
				});
			}
		},
		[hotelID, onRefetch],
	);

	const reset = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		dailyStatus: viewModel,
		onSetDailyStatus: onSetViewModel,

		reset,

		refetchRoomPriceRankDailyStatus,
	};
};
